import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Flame,
  Info,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  Wind,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAppStore } from '@/lib/store'
import { haptic } from '@/lib/telegram'
import type { ProductSpec } from '@/shared/types'

export interface EvolutionStage {
  stage: 'top' | 'heart' | 'base'
  time_range: string
  time_range_en: string
  title_uk: string
  title_en: string
  headline_uk: string
  headline_en: string
  notes: string[]
  vector: {
    freshness: number
    sharpness: number
    sillage: number
    sweetness: number
    formality: number
  }
  intensity_pct: number
  sillage_radius_uk: string
  sillage_radius_en: string
  color_glow: string
  atmosphere_uk: string
  atmosphere_en: string
}

export interface EvolutionData {
  product_id: number | string
  name_uk: string
  name_en: string | null
  base_vector: Record<string, number>
  dominant_chords: string[]
  all_notes: string[]
  stages: EvolutionStage[]
}

interface Props {
  productId: number | string
  specs?: ProductSpec[]
  isMayParfum?: boolean
}

const TOTAL_CYCLE_MS = 12000 // 12 seconds full video playback cycle (4s per stage)

export function ScentEvolutionPlayer({ productId, specs = [], isMayParfum = false }: Props) {
  const lang = useAppStore((s) => s.lang)

  // Fetch evolution from backend
  const { data: evoData } = useQuery<EvolutionData>({
    queryKey: ['fragrance-evolution', productId],
    queryFn: () => api.get<EvolutionData>(`/products/${productId}/evolution`),
    staleTime: 5 * 60_000,
  })

  // Fallback heuristic stages if backend is loading or unavailable
  const fallbackStages: EvolutionStage[] = [
    {
      stage: 'top',
      time_range: '0–15 хв',
      time_range_en: '0–15 min',
      title_uk: 'Верхні ноти • Старт композиції',
      title_en: 'Top Notes • Opening Phase',
      headline_uk: 'Іскристий спалах та перша дифузія',
      headline_en: 'Sparkling explosion & initial projection',
      notes: extractNotesFromSpecs(specs, 'top'),
      vector: { freshness: 0.25, sharpness: 0.65, sillage: 0.85, sweetness: 0.40, formality: 0.35 },
      intensity_pct: 100,
      sillage_radius_uk: '2–3 метри (активний шлейф)',
      sillage_radius_en: '2–3 meters (high sillage cloud)',
      color_glow: '#38bdf8',
      atmosphere_uk: 'Перші хвилини нанесення: леткі цитрусові, альдегідні або свіжі молекули створюють миттєвий шлейф високої дифузії.',
      atmosphere_en: 'Initial minutes after application: volatile citrus and fresh molecules ignite an immediate sparkling trail.',
    },
    {
      stage: 'heart',
      time_range: '1–3 год',
      time_range_en: '1–3 hours',
      title_uk: 'Ноти серця • Кульмінація',
      title_en: 'Heart Notes • Core Bloom',
      headline_uk: 'Розквіт справжнього характеру аромату',
      headline_en: 'Full bloom of the core character',
      notes: extractNotesFromSpecs(specs, 'heart'),
      vector: { freshness: 0.50, sharpness: 0.50, sillage: 0.65, sweetness: 0.55, formality: 0.60 },
      intensity_pct: 85,
      sillage_radius_uk: '1.5–2 метри (вишукана аура)',
      sillage_radius_en: '1.5–2 meters (refined cloud)',
      color_glow: '#c9a24b',
      atmosphere_uk: 'Стабілізація на теплій шкірі: квіти, спеції та смоли розкривають головну мелодію та індивідуальність парфуму.',
      atmosphere_en: 'Stabilization on skin: florals, spices, and resins reveal the true signature and harmony of the fragrance.',
    },
    {
      stage: 'base',
      time_range: '4–8+ год',
      time_range_en: '4–8+ hours',
      title_uk: 'Базові ноти • Шлейф & Drydown',
      title_en: 'Base Notes • Tenacious Drydown',
      headline_uk: 'Глибокий інтимний акорд та фіксатори',
      headline_en: 'Deep intimate resonance & skin trail',
      notes: extractNotesFromSpecs(specs, 'base'),
      vector: { freshness: 0.80, sharpness: 0.40, sillage: 0.40, sweetness: 0.70, formality: 0.80 },
      intensity_pct: 65,
      sillage_radius_uk: 'До 0.5 метра (інтимний кокон на шкірі)',
      sillage_radius_en: 'Skin scent / intimate cocoon (up to 0.5 m)',
      color_glow: '#f59e0b',
      atmosphere_uk: 'Стійкі фіксатори (амбра, мускус, уд, ветивер) зливаються зі шкірою, залишаючи магнетичний і тривалий післясмак.',
      atmosphere_en: 'Tenacious fixatives (amber, musk, oud, cedar) lock onto the skin into a warm, sensual, magnetic trail.',
    },
  ]

  const stages = evoData?.stages && evoData.stages.length > 0 ? evoData.stages : fallbackStages

  // Video playback state: progress from 0.0 to 1.0
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0) // 0 to 1
  const animRef = useRef<number | null>(null)
  const lastTimeRef = useRef<number | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Current active stage derived from progress
  const stageIndex = Math.min(2, Math.floor(progress * 3))
  const currentStage = stages[stageIndex] || stages[0]

  // Toggle Play / Pause
  const handleTogglePlay = () => {
    haptic('medium')
    setIsPlaying((prev) => !prev)
  }

  // Restart from beginning
  const handleRestart = () => {
    haptic('light')
    setProgress(0)
    setIsPlaying(true)
  }

  // Manual Stage Jump
  const handleJumpToStage = (idx: number) => {
    haptic('light')
    setProgress(idx / 3 + 0.01)
  }

  // Animation frame loop for video playback
  useEffect(() => {
    if (!isPlaying) {
      lastTimeRef.current = null
      if (animRef.current) cancelAnimationFrame(animRef.current)
      return
    }

    const step = (time: number) => {
      if (lastTimeRef.current !== null) {
        const delta = time - lastTimeRef.current
        setProgress((prev) => {
          const next = prev + delta / TOTAL_CYCLE_MS
          if (next >= 1) {
            return 0 // loop playback
          }
          return next
        })
      }
      lastTimeRef.current = time
      animRef.current = requestAnimationFrame(step)
    }

    animRef.current = requestAnimationFrame(step)
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
    }
  }, [isPlaying])

  // Canvas visualizer rendering (aura rings & rising fragrance molecules)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let particleAnimId: number
    let angle = 0

    // Generate lightweight floating particles
    const particles = Array.from({ length: 28 }, (_, i) => ({
      x: 140 + Math.cos(i * 1.3) * 60,
      y: 100 + Math.sin(i * 1.5) * 50,
      r: 1.5 + (i % 3),
      speedY: 0.3 + (i % 4) * 0.2,
      opacity: 0.2 + (i % 5) * 0.15,
      hueOffset: (i * 15) % 40,
    }))

    const render = () => {
      angle += 0.02
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const w = canvas.width
      const h = canvas.height
      const cx = w / 2
      const cy = h / 2

      // Background subtle gradient
      const bgGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, 130)
      const glowColor = currentStage.color_glow
      bgGrad.addColorStop(0, `${glowColor}25`)
      bgGrad.addColorStop(0.6, `${glowColor}08`)
      bgGrad.addColorStop(1, 'transparent')
      ctx.fillStyle = bgGrad
      ctx.fillRect(0, 0, w, h)

      // Dynamic Sillage Halo rings based on stage intensity
      const radiusBase = currentStage.stage === 'top' ? 68 : currentStage.stage === 'heart' ? 52 : 38
      for (let rIdx = 1; rIdx <= 3; rIdx++) {
        const pulseR = radiusBase * (rIdx * 0.5) + Math.sin(angle * 1.8 + rIdx) * 3
        ctx.beginPath()
        ctx.arc(cx, cy, pulseR, 0, Math.PI * 2)
        ctx.strokeStyle = `${glowColor}${rIdx === 1 ? '50' : '20'}`
        ctx.lineWidth = rIdx === 1 ? 1.5 : 1
        ctx.stroke()
      }

      // Draw floating perfume molecules (evaporation physics)
      particles.forEach((p) => {
        p.y -= p.speedY
        if (p.y < 10) {
          p.y = h - 20
          p.x = cx + (Math.random() - 0.5) * 90
        }
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.fillStyle = `${glowColor}${Math.floor(p.opacity * 255).toString(16).padStart(2, '0')}`
        ctx.fill()
      })

      // Central Perfume Atom Heart
      ctx.beginPath()
      ctx.arc(cx, cy, 18, 0, Math.PI * 2)
      ctx.fillStyle = `${glowColor}40`
      ctx.fill()
      ctx.strokeStyle = glowColor
      ctx.lineWidth = 2
      ctx.stroke()

      // Core icon or text inside atom
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 10px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const label = currentStage.stage === 'top' ? 'TOP' : currentStage.stage === 'heart' ? 'MID' : 'BASE'
      ctx.fillText(label, cx, cy)

      particleAnimId = requestAnimationFrame(render)
    }

    render()
    return () => {
      cancelAnimationFrame(particleAnimId)
    }
  }, [currentStage])

  // If no specs and no evo data with notes, don't show empty block
  const hasAnyNotes = stages.some((s) => s.notes && s.notes.length > 0)
  if (!hasAnyNotes && specs.length === 0) {
    return null
  }

  const vec = currentStage.vector

  return (
    <section className={`mt-8 rounded-3xl border p-4 sm:p-6 transition-all relative overflow-hidden shadow-2xl ${
      isMayParfum
        ? 'border-[#c9a24b]/40 bg-gradient-to-b from-[#1f242d] to-[#171b22] text-[#f8f3ea]'
        : 'border-white/10 bg-[#161922] text-slate-100'
    }`}>
      {/* Decorative ambient backdrop */}
      <div
        className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl opacity-20 transition-all duration-700"
        style={{ backgroundColor: currentStage.color_glow }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#d7b171]">
            <Sparkles className="h-4 w-4 text-[#d7b171]" />
            <span>
              {lang === 'en'
                ? 'Time Evolution & Olfactory Pyramid'
                : lang === 'ru'
                ? 'Динамика раскрытия аромата во времени'
                : 'Динаміка розкриття аромату в часі'}
            </span>
          </div>
          <p className="mt-1 text-xs text-neutral-400">
            {lang === 'en'
              ? 'Watch how the fragrance notes unfold and project across hours on your skin.'
              : lang === 'ru'
              ? 'Смотрите, как раскрываются ноты пирамиды и меняется шлейф с каждым часом.'
              : 'Парфуми змінюються з кожною годиною. Спостерігайте, як розкриваються ноти та трансформується шлейф.'}
          </p>
        </div>

        {/* Video Player Action Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleTogglePlay}
            className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-xs font-bold transition shadow-lg active:scale-95 ${
              isPlaying
                ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30'
                : 'bg-[#c9a24b] text-[#110f18] hover:bg-[#d8b25c]'
            }`}
          >
            {isPlaying ? (
              <>
                <Pause className="h-3.5 w-3.5 fill-current" />
                <span>{lang === 'en' ? 'Pause' : 'Пауза'}</span>
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 fill-current" />
                <span>{lang === 'en' ? 'Play Evolution' : 'Запустити відео'}</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleRestart}
            aria-label="Restart"
            className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-neutral-300 hover:text-white hover:bg-white/10 transition"
            title={lang === 'en' ? 'Rewind to start' : 'З початку'}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Scrubbable Timeline Video Track */}
      <div className="space-y-1.5 my-4">
        <div
          role="slider"
          tabIndex={0}
          aria-label="Scrub timeline"
          aria-valuenow={Math.round(progress * 100)}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            const clickPos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
            haptic('light')
            setProgress(clickPos)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              haptic('light')
              setProgress((p) => Math.min(1, p + 0.1))
            } else if (e.key === 'ArrowLeft') {
              haptic('light')
              setProgress((p) => Math.max(0, p - 0.1))
            }
          }}
          className="relative h-3 w-full rounded-full bg-black/40 border border-white/10 cursor-pointer overflow-hidden group"
        >
          {/* Progress fill */}
          <div
            className="h-full transition-all duration-100 ease-linear rounded-full"
            style={{
              width: `${Math.round(progress * 100)}%`,
              backgroundColor: currentStage.color_glow,
              boxShadow: `0 0 12px ${currentStage.color_glow}`,
            }}
          />
          {/* Phase dividers (at 33% and 66%) */}
          <div className="absolute left-1/3 top-0 bottom-0 w-0.5 bg-white/20" />
          <div className="absolute left-2/3 top-0 bottom-0 w-0.5 bg-white/20" />
        </div>

        {/* Timeline markers */}
        <div className="flex justify-between text-[10px] font-mono text-neutral-400 px-1">
          <span className={stageIndex === 0 ? 'text-sky-300 font-bold' : ''}>0 хв (Старт)</span>
          <span className={stageIndex === 0 ? 'text-sky-300 font-bold' : ''}>15 хв</span>
          <span className={stageIndex === 1 ? 'text-[#d7b171] font-bold' : ''}>1–3 год (Серце)</span>
          <span className={stageIndex === 2 ? 'text-amber-300 font-bold' : ''}>4–8+ год (База / Drydown)</span>
        </div>
      </div>

      {/* Interactive Phase Selectors (3 Pills) */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {stages.map((stage, idx) => {
          const isActive = stageIndex === idx
          const Icon = idx === 0 ? Wind : idx === 1 ? Sparkles : Flame
          return (
            <button
              key={stage.stage}
              type="button"
              onClick={() => handleJumpToStage(idx)}
              className={`flex flex-col items-center justify-center p-2.5 rounded-2xl border text-center transition-all ${
                isActive
                  ? 'border-[#c9a24b] bg-white/[0.08] shadow-lg scale-[1.02]'
                  : 'border-white/10 bg-white/[0.02] text-neutral-400 hover:text-white hover:border-white/20'
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon
                  className="h-3.5 w-3.5"
                  style={{ color: isActive ? stage.color_glow : undefined }}
                />
                <span className="text-[11px] font-bold" style={{ color: isActive ? '#ffffff' : undefined }}>
                  {stage.time_range}
                </span>
              </div>
              <span className="text-[10px] text-neutral-400 truncate max-w-full">
                {idx === 0 ? (lang === 'en' ? 'Top' : 'Верхні') : idx === 1 ? (lang === 'en' ? 'Heart' : 'Серце') : (lang === 'en' ? 'Base' : 'База')}
              </span>
            </button>
          )
        })}
      </div>

      {/* Visual Canvas & Dynamic Olfactory Aura Screen */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center bg-black/30 border border-white/10 rounded-2xl p-4">
        {/* Animated Visualizer Halo (Left Column) */}
        <div className="md:col-span-5 flex flex-col items-center justify-center">
          <canvas
            ref={canvasRef}
            width={280}
            height={160}
            className="w-full max-w-[280px] h-[160px] block"
          />
          <div className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-neutral-300">
            <span
              className="inline-block h-2 w-2 rounded-full animate-ping"
              style={{ backgroundColor: currentStage.color_glow }}
            />
            <span>
              {lang === 'en' ? currentStage.sillage_radius_en : currentStage.sillage_radius_uk}
            </span>
          </div>
        </div>

        {/* Phase Details & Notes (Right Column) */}
        <div className="md:col-span-7 space-y-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                style={{
                  color: currentStage.color_glow,
                  borderColor: `${currentStage.color_glow}60`,
                  backgroundColor: `${currentStage.color_glow}15`,
                }}
              >
                {currentStage.time_range}
              </span>
              <h4 className="text-sm font-bold text-white">
                {lang === 'en' ? currentStage.title_en : currentStage.title_uk}
              </h4>
            </div>
            <p className="text-xs font-medium text-[#d7b171] mt-1">
              {lang === 'en' ? currentStage.headline_en : currentStage.headline_uk}
            </p>
          </div>

          {/* Active Notes Pills */}
          <div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-neutral-400 block mb-1.5">
              {lang === 'en' ? 'Active Pyramid Notes:' : 'Ноти, що звучать у цей період:'}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {currentStage.notes.length > 0 ? (
                currentStage.notes.map((note, nIdx) => (
                  <span
                    key={nIdx}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-xl border transition shadow-sm"
                    style={{
                      borderColor: `${currentStage.color_glow}80`,
                      backgroundColor: `${currentStage.color_glow}18`,
                      color: '#ffffff',
                    }}
                  >
                    <span>✨</span>
                    <span>{note}</span>
                  </span>
                ))
              ) : (
                <span className="text-xs text-neutral-500 italic">
                  {lang === 'en' ? 'Harmonized bouquet' : 'Гармонізований ольфакторний букет'}
                </span>
              )}
            </div>
          </div>

          {/* Olfactory Atmospheric Narrative */}
          <p className="text-xs leading-relaxed text-neutral-300 bg-white/[0.03] border border-white/5 rounded-xl p-2.5">
            {lang === 'en' ? currentStage.atmosphere_en : currentStage.atmosphere_uk}
          </p>

          {/* Sensory Vector Balance Bars */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-white/10 text-[10px]">
            <div>
              <div className="flex justify-between text-neutral-400 mb-0.5">
                <span>{lang === 'en' ? 'Freshness' : 'Свіжість'}</span>
                <span className="font-mono text-white">{Math.round(vec.freshness * 100)}%</span>
              </div>
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-sky-400 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round(vec.freshness * 100)}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between text-neutral-400 mb-0.5">
                <span>{lang === 'en' ? 'Sillage' : 'Шлейф'}</span>
                <span className="font-mono text-white">{Math.round(vec.sillage * 100)}%</span>
              </div>
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#c9a24b] rounded-full transition-all duration-300"
                  style={{ width: `${Math.round(vec.sillage * 100)}%` }}
                />
              </div>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <div className="flex justify-between text-neutral-400 mb-0.5">
                <span>{lang === 'en' ? 'Sweetness' : 'Солодкість'}</span>
                <span className="font-mono text-white">{Math.round(vec.sweetness * 100)}%</span>
              </div>
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round(vec.sweetness * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Helpful Hint */}
      <div className="mt-3 flex items-center justify-between text-[11px] text-neutral-400 px-1">
        <span className="flex items-center gap-1">
          <Info className="h-3 w-3 text-[#c9a24b]" />
          <span>
            {lang === 'en'
              ? 'Tip: Press "Play Evolution" to see how the perfume transforms over time'
              : 'Порада: Натисніть "Запустити відео", щоб автоматично побачити метаморфозу парфуму'}
          </span>
        </span>
      </div>
    </section>
  )
}

function extractNotesFromSpecs(specs: ProductSpec[], stage: 'top' | 'heart' | 'base'): string[] {
  const result: string[] = []
  const stageKeywords = {
    top: ['верхн', 'початков', 'top'],
    heart: ['серц', 'heart', 'middle'],
    base: ['базов', 'кінцев', 'base', 'шлейф'],
  }
  const keywords = stageKeywords[stage]

  for (const spec of specs) {
    const name = (spec.name_uk || '').toLowerCase()
    const code = (spec.code || '').toLowerCase()
    if (keywords.some((k) => name.includes(k) || code.includes(k)) && spec.value_uk) {
      for (const part of spec.value_uk.split(',')) {
        const cleaned = part.trim()
        if (cleaned && !result.includes(cleaned)) {
          result.push(cleaned)
        }
      }
    }
  }

  // Fallback: if no stage-specific specs found, check generic "ноти"
  if (result.length === 0) {
    const allNotes: string[] = []
    for (const spec of specs) {
      const name = (spec.name_uk || '').toLowerCase()
      if (name.includes('нот') && spec.value_uk) {
        for (const part of spec.value_uk.split(',')) {
          const cleaned = part.trim()
          if (cleaned && !allNotes.includes(cleaned)) {
            allNotes.push(cleaned)
          }
        }
      }
    }
    if (stage === 'top') return allNotes.slice(0, Math.max(1, Math.ceil(allNotes.length / 3)))
    if (stage === 'base') return allNotes.slice(-Math.max(1, Math.ceil(allNotes.length / 3)))
    return allNotes.slice(Math.ceil(allNotes.length / 3), -Math.max(1, Math.ceil(allNotes.length / 3))) || allNotes
  }

  return result
}
